<script lang="ts">
	// UC-36. Help, as the Owner Dashboard - Setup page draws D22: Bosla's
	// walkthroughs in the dashboard's language, a section at a time, and one
	// walkthrough playing with its captions and its scenes.
	//
	// The server picks the walkthroughs and their order, and hands a link to
	// each video that works for fifteen minutes. A link that ran out, often while
	// the video sat paused, is asked for again, and the video goes on from the
	// same second with nothing said (help/playback.ts). Nothing here is
	// stored: not the second, not what was watched.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Icon from '../components/Icon.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import VideoPlayer, { type PlayerLabels } from '../components/VideoPlayer.svelte';
	import { untrack } from 'svelte';
	import { directionOf, fill, isArabic, langTag, message, type Language } from '../i18n/messages';
	import { loadHelp, type HelpAnswer } from '../help/load';
	import { afterError, loaded, noLink, opening, STALL_MS, type Playback } from '../help/playback';
	import { clock, playerScenes, recordingOf, sceneAt } from '../help/player';
	import {
		scenesKey,
		sectionNames,
		surfaceNames,
		type HelpSection,
		type HelpSectionName,
		type Walkthrough,
		videosKey,
	} from '../help/shelf';

	interface Props {
		language: Language;
	}

	let { language }: Props = $props();

	// Left-to-right isolate and its end, by code point: they keep a number in
	// order inside Arabic, and the compiler rightly refuses them in source.
	const ISOLATE_LTR = String.fromCodePoint(0x2066);
	const POP_ISOLATE = String.fromCodePoint(0x2069);
	const isolated = (n: number) => `${ISOLATE_LTR}${n}${POP_ISOLATE}`;

	let sections = $state<HelpSection[] | null>(null);
	// The last ask got no answer; Retry shows when asking again can help.
	let failure = $state<{ retry: boolean } | null>(null);
	let pending = $state(false);
	// A wait shows only after 300 ms.
	let waitShown = $state(false);
	let asked = 0;
	// When the links in hand were signed, near enough: when their answer came.
	// Wall time, as S3 counts a link's life, and on through a computer's sleep.
	let answeredAt = 0;

	// Asks for Help; the answer replaces the last one, links and all. Null when
	// a newer ask has taken its place.
	async function list(): Promise<HelpAnswer | null> {
		const ask = ++asked;
		pending = true;
		const timer = setTimeout(() => {
			if (ask === asked) waitShown = true;
		}, 300);
		const answer = await loadHelp(language);
		clearTimeout(timer);
		if (ask !== asked) return null;
		pending = false;
		waitShown = false;
		if (answer.kind === 'failed') {
			failure = { retry: answer.retry };
			return answer;
		}
		failure = null;
		sections = answer.sections;
		answeredAt = Date.now();
		return answer;
	}

	// Another language is other recordings: back to the list, and ask again.
	$effect(() => {
		void language;
		untrack(() => {
			leave();
			sections = null;
			failure = null;
			void list();
		});
	});

	let chosen = $state<HelpSectionName | null>(null);
	const shelf = $derived(sections ?? []);
	const section = $derived(shelf.find((s) => s.section === chosen) ?? shelf[0]);

	// The walkthrough playing, by id, so a fresh answer brings its fresh link.
	let openId = $state<string | null>(null);
	const watching = $derived(
		openId ? section?.walkthroughs.find((w) => w.id === openId) : undefined,
	);
	const scenes = $derived(watching ? playerScenes(watching) : []);

	let playback = $state<Playback>(opening());
	let videoWait = $state(false);
	let videoTimer: ReturnType<typeof setTimeout> | undefined;
	let stallTimer: ReturnType<typeof setTimeout> | undefined;
	// How many times a video has loaded, so a stall can tell whether one did.
	let loads = 0;
	let captions = $state(true);
	// Seconds into the video, which the player keeps; the scene playing follows.
	let time = $state(0);
	const scene = $derived(sceneAt(scenes, time));
	let pick = $state<{ at: number; key: number } | null>(null);

	// A video is loading, at first or on a fresh link. The wait shows after
	// 300 ms, while nothing is on screen yet, and a load that has not finished
	// after STALL_MS failed for the network.
	function startWait() {
		videoWait = false;
		clearTimeout(videoTimer);
		clearTimeout(stallTimer);
		videoTimer = setTimeout(() => (videoWait = true), 300);
		const forId = openId;
		const before = loads;
		stallTimer = setTimeout(() => {
			if (openId === forId && loads === before && playback.state !== 'error') {
				void relink(true);
			}
		}, STALL_MS);
	}

	function stopWait() {
		clearTimeout(videoTimer);
		clearTimeout(stallTimer);
		videoWait = false;
	}

	$effect(() => stopWait);

	function pickSection(name: HelpSectionName) {
		chosen = name;
		openId = null;
	}

	function open(w: Walkthrough) {
		chosen = section?.section ?? null;
		openId = w.id;
		time = 0;
		pick = null;
		playback = opening();
		startWait();
	}

	function leave() {
		openId = null;
		pick = null;
		stopWait();
	}

	function ready() {
		loads++;
		playback = loaded();
		stopWait();
	}

	// The video stopped. A link out for a while may have run out: ask for a
	// new one, and go on with it. Anything else is said in place.
	async function relink(network: boolean) {
		const { next, relink: again } = afterError(playback, Date.now() - answeredAt, network);
		playback = next;
		if (!again) return;
		// Its own ask is under way; a stall while it waits must not start another.
		clearTimeout(stallTimer);
		const forId = openId;
		const failed = watching?.link;
		const answer = await list();
		// A newer ask, or another walkthrough opened meanwhile, takes over.
		if (answer === null || openId !== forId) return;
		if (answer.kind === 'failed') playback = noLink(answer.retry);
		// The new week's run dropped it, or moved it to another section.
		else if (!watching) leave();
		// The very link that failed: no new link came.
		else if (watching.link === failed) playback = noLink(false);
		else {
			fromStartIfNew(failed);
			startWait();
		}
	}

	// A new week's recording is another video: it starts from its start, and
	// nothing picked in the last one carries over.
	function fromStartIfNew(failed: string | undefined) {
		if (watching && recordingOf(watching.link) !== recordingOf(failed)) {
			time = 0;
			pick = null;
		}
	}

	async function retry() {
		const forId = openId;
		const failed = watching?.link;
		const answer = await list();
		if (answer === null || openId !== forId) return;
		if (answer.kind === 'failed') {
			playback = noLink(answer.retry);
			return;
		}
		// The new week's run dropped it, or moved it to another section.
		if (!watching) {
			leave();
			return;
		}
		fromStartIfNew(failed);
		playback = opening();
		startWait();
	}

	function playScene(i: number) {
		pick = { at: scenes[i]?.at ?? 0, key: (pick?.key ?? 0) + 1 };
	}

	const labels = $derived<PlayerLabels>({
		play: message(language, 'helpPlay'),
		pause: message(language, 'helpPause'),
		captions: message(language, 'helpCaptions'),
		fullscreen: message(language, 'helpFullscreen'),
		exitFullscreen: message(language, 'helpExitFullscreen'),
		seek: message(language, 'helpSeek'),
	});

	const sectionName = $derived(section ? message(language, sectionNames[section.section]) : '');
</script>

<div class="help" data-testid="panel-help">
	{#if watching && section}
		<div class="back">
			<Button
				testId="help-back"
				label={sectionName}
				icon="back"
				mirrorIcon={!isArabic(language)}
				tone="text-quiet"
				size="sm"
				onPress={leave}
			/>
		</div>
		<div class="head">
			<h2 data-testid="help-title">{watching.title}</h2>
			<p class="meta">
				<span class="nowrap">{message(language, surfaceNames[watching.surface])}</span>
				<bdi class="len" dir="ltr">{clock(watching.durationMs / 1000)}</bdi>
				<span class="nowrap">
					{fill(message(language, scenesKey(scenes.length)), isolated(scenes.length))}
				</span>
			</p>
		</div>
		<div class="watch">
			<div class="video">
				<!-- A new week's recording mounts a player of its own, from its start. -->
				{#key recordingOf(watching.link)}
					<VideoPlayer
						testId="help-player"
						src={watching.link}
						title={watching.title}
						duration={watching.durationMs / 1000}
						{scenes}
						dir={directionOf(language)}
						lang={langTag(language)}
						state={playback.state}
						{captions}
						onCaptions={(on) => (captions = on)}
						startAt={pick?.at ?? 0}
						startKey={pick?.key}
						bind:time
						onReady={ready}
						onLinkExpired={(_, network) => void relink(network)}
						{labels}
					>
						{#if playback.state === 'loading' && videoWait}
							<div class="wait">
								<Loading
									size="lg"
									label={message(language, 'helpVideoLoading')}
									caption={message(language, 'helpVideoLoading')}
									testId="help-video-loading"
								/>
							</div>
						{:else if playback.state === 'error'}
							<div class="failed">
								<p data-testid="help-video-failed">{message(language, 'helpVideoFailed')}</p>
								{#if playback.retry}
									<Button
										testId="help-video-retry"
										label={message(language, 'helpRetry')}
										tone="secondary"
										size="sm"
										onPress={() => void retry()}
									/>
								{/if}
							</div>
						{/if}
					</VideoPlayer>
				{/key}
			</div>
			<div class="side">
				<Card title={message(language, 'helpScenes')} testId="help-scenes">
					<ol class="rows scenes">
						{#each scenes as s, i (i)}
							<li>
								<Row
									as="button"
									current={i === scene}
									onPress={() => playScene(i)}
									testId="help-scene-{i + 1}"
								>
									<span class="n">{i + 1}</span>
									<span class="caption">{s.caption}</span>
									<span class="at" dir="ltr">{clock(s.at)}</span>
								</Row>
							</li>
						{/each}
					</ol>
				</Card>
			</div>
		</div>
	{:else if !sections && pending && waitShown}
		<div class="loading">
			<Loading size="lg" label={message(language, 'loading')} testId="help-loading" />
		</div>
	{:else if !sections && failure}
		<Band
			tone="flag"
			action={failure.retry ? message(language, 'helpRetry') : undefined}
			onAction={() => void list()}
			testId="help-error"
			actionTestId="help-retry"
		>
			{message(language, 'loadFailed')}
		</Band>
	{:else if sections}
		<p class="intro">{message(language, 'helpIntro')}</p>
		{#if shelf.length > 0}
			<div class="sections" role="group" aria-label={message(language, 'helpSectionLabel')}>
				{#each shelf as s (s.section)}
					<Button
						testId="help-section-{s.section}"
						label={message(language, sectionNames[s.section])}
						tone={s.section === section?.section ? 'ink' : 'secondary'}
						pressed={s.section === section?.section}
						size="sm"
						onPress={() => pickSection(s.section)}
					/>
				{/each}
			</div>
		{/if}
		<div class="list">
			{#if section}
				<Card
					title={sectionName}
					note={fill(
						message(language, videosKey(section.walkthroughs.length)),
						isolated(section.walkthroughs.length),
					)}
					testId="help-list"
				>
					<div class="rows walkthroughs">
						{#each section.walkthroughs as w (w.id)}
							<Row as="button" onPress={() => open(w)} testId="help-walkthrough-{w.id}">
								<span class="disc"><Icon name="play" size={18} /></span>
								<span class="names">
									<span class="title">{w.title}</span>
									<span class="where">{message(language, surfaceNames[w.surface])}</span>
								</span>
								<span class="len" dir="ltr">{clock(w.durationMs / 1000)}</span>
							</Row>
						{/each}
					</div>
				</Card>
			{:else}
				<Card title={message(language, 'ownerHelp')} testId="help-list">
					<p class="empty" data-testid="help-empty">{message(language, 'helpEmpty')}</p>
				</Card>
			{/if}
		</div>
	{/if}
</div>

<style>
	.help {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.intro {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.sections {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	.list {
		max-inline-size: 760px;
	}

	.loading {
		display: flex;
		padding-block: var(--space-11);
	}

	.walkthroughs {
		--cols: auto minmax(0, 1fr) auto;
	}

	.scenes {
		--cols: 2ch minmax(0, 1fr) auto;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.disc {
		display: grid;
		place-items: center;
		inline-size: var(--ctl-min);
		block-size: var(--ctl-min);
		border-radius: 50%;
		border: var(--border-width) solid var(--border-default);
	}

	.names {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.title {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.where {
		font-size: var(--text-2xs);
		color: var(--text-body);
	}

	.len {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.back {
		display: flex;
	}

	.head {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
		letter-spacing: var(--tracking-snug);
		text-wrap: balance;
	}

	.meta {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-2) var(--space-4);
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.nowrap {
		white-space: nowrap;
	}

	.watch {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: var(--grid-gap);
	}

	.video {
		flex: 999 1 560px;
		min-inline-size: 0;
	}

	.side {
		flex: 1 1 280px;
		min-inline-size: 0;
	}

	.wait {
		display: flex;
		justify-content: center;
		white-space: nowrap;
		min-inline-size: max-content;
	}

	.failed {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-4);
	}

	.failed p {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.n {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.caption {
		min-inline-size: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.at {
		font-family: var(--font-mono);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	/* The scene playing reads strong, as the D22 page draws it. */
	.scenes :global([aria-current='true'] .n),
	.scenes :global([aria-current='true'] .caption),
	.scenes :global([aria-current='true'] .at) {
		color: var(--text-strong);
		font-weight: 700;
	}
</style>
